'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Control-code names from ASCII (ANSI X3.4-1986) with their caret notation.
const CONTROL: Array<[string, string]> = [
  ['NUL', 'Null'],
  ['SOH', 'Start of heading'],
  ['STX', 'Start of text'],
  ['ETX', 'End of text'],
  ['EOT', 'End of transmission'],
  ['ENQ', 'Enquiry'],
  ['ACK', 'Acknowledge'],
  ['BEL', 'Bell'],
  ['BS', 'Backspace'],
  ['HT', 'Horizontal tab'],
  ['LF', 'Line feed (new line)'],
  ['VT', 'Vertical tab'],
  ['FF', 'Form feed'],
  ['CR', 'Carriage return'],
  ['SO', 'Shift out'],
  ['SI', 'Shift in'],
  ['DLE', 'Data link escape'],
  ['DC1', 'Device control 1 (XON)'],
  ['DC2', 'Device control 2'],
  ['DC3', 'Device control 3 (XOFF)'],
  ['DC4', 'Device control 4'],
  ['NAK', 'Negative acknowledge'],
  ['SYN', 'Synchronous idle'],
  ['ETB', 'End of transmission block'],
  ['CAN', 'Cancel'],
  ['EM', 'End of medium'],
  ['SUB', 'Substitute'],
  ['ESC', 'Escape'],
  ['FS', 'File separator'],
  ['GS', 'Group separator'],
  ['RS', 'Record separator'],
  ['US', 'Unit separator'],
];

const NAMED: Record<number, string> = {
  32: 'Space',
  34: '&quot;',
  38: '&amp;',
  39: '&apos;',
  60: '&lt;',
  62: '&gt;',
  127: 'Delete',
  160: '&nbsp;',
  169: '&copy;',
  174: '&reg;',
  128: '&euro;',
};

export type AsciiRow = {
  dec: number;
  hex: string;
  oct: string;
  bin: string;
  char: string;
  html: string;
  desc: string;
};

function buildRows(): AsciiRow[] {
  const cp1252 = new TextDecoder('windows-1252');
  const rows: AsciiRow[] = [];
  for (let d = 0; d < 256; d += 1) {
    const control = d < 32 ? CONTROL[d] : d === 127 ? ['DEL', 'Delete'] : null;
    const char = control
      ? control[0]
      : d < 128
        ? String.fromCharCode(d)
        : cp1252.decode(new Uint8Array([d]));
    const undefinedIn1252 = d >= 128 && d < 160 && char.charCodeAt(0) === d;
    rows.push({
      dec: d,
      hex: d.toString(16).toUpperCase().padStart(2, '0'),
      oct: d.toString(8).padStart(3, '0'),
      bin: d.toString(2).padStart(8, '0'),
      char: undefinedIn1252 ? '' : char,
      html:
        control || undefinedIn1252
          ? d === 9 || d === 10 || d === 13
            ? `&#${d};`
            : ''
          : NAMED[d]?.startsWith('&')
            ? NAMED[d]
            : `&#${char.codePointAt(0)};`,
      desc: control
        ? `${control[1]}${d < 32 ? ` (Ctrl+${String.fromCharCode(d + 64)})` : ''}`
        : undefinedIn1252
          ? 'Unused in Windows-1252'
          : d === 32
            ? 'Space'
            : d >= 128
              ? `U+${char.codePointAt(0)?.toString(16).toUpperCase().padStart(4, '0')}`
              : /[0-9]/.test(char)
                ? `Digit ${char}`
                : /[A-Z]/.test(char)
                  ? `Uppercase ${char}`
                  : /[a-z]/.test(char)
                    ? `Lowercase ${char}`
                    : 'Punctuation / symbol',
    });
  }
  return rows;
}

export default function BrowserAsciiTable({ config }: { config?: { toolSlug?: string } }) {
  const rows = useMemo(() => buildRows(), []);
  const [query, setQuery] = useState('');
  const [range, setRange] = useState<'std' | 'all' | 'ext'>('std');
  const [copied, setCopied] = useState<string | null>(null);
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'ascii-table', '').catch(() => undefined);
  };

  const shown = useMemo(() => {
    const q = query.trim();
    const base = rows.filter((r) =>
      range === 'std' ? r.dec < 128 : range === 'ext' ? r.dec >= 128 : true,
    );
    if (!q) return base;
    const lower = q.toLowerCase();
    return base.filter(
      (r) =>
        r.char === q ||
        String(r.dec) === q ||
        r.hex.toLowerCase() === lower.replace(/^0x/, '') ||
        r.bin === q.padStart(8, '0') ||
        (q.length > 1 && r.desc.toLowerCase().includes(lower)) ||
        (q.length > 1 && r.html.toLowerCase().includes(lower)),
    );
  }, [rows, query, range]);

  const copy = async (text: string) => {
    touch();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      window.setTimeout(() => setCopied(null), 1200);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Reference</span>
        <h2 className="tool-live-title">ASCII table</h2>
        <p className="tool-live-subtitle">
          Decimal, hex, octal, binary, character and HTML code for every ASCII value. Search by any
          of them; click a cell to copy it.
        </p>
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <label className="tool-live-field" style={{ flex: '1 1 220px' }}>
          <span>Search: a character, 65, 0x41, 01000001, or a name</span>
          <input
            onChange={(e) => {
              touch();
              setQuery(e.target.value);
            }}
            placeholder="A"
            type="text"
            value={query}
          />
        </label>
        <label className="tool-live-field">
          <span>Show</span>
          <select onChange={(e) => setRange(e.target.value as typeof range)} value={range}>
            <option value="std">Standard ASCII 0-127</option>
            <option value="ext">Extended 128-255 (Windows-1252)</option>
            <option value="all">All 0-255</option>
          </select>
        </label>
      </div>
      {copied !== null ? <p className="status-message status-success">Copied {copied}</p> : null}
      <div className="tool-data-table-shell">
        <table className="tool-data-table" style={{ fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr>
              <th scope="col">Dec</th>
              <th scope="col">Hex</th>
              <th scope="col">Oct</th>
              <th scope="col">Binary</th>
              <th scope="col">Char</th>
              <th scope="col">HTML</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.dec}>
                {[r.dec, r.hex, r.oct, r.bin].map((v, i) => (
                  <td key={i} onClick={() => void copy(String(v))} style={{ cursor: 'copy' }}>
                    {v}
                  </td>
                ))}
                <td
                  onClick={() => r.char && void copy(r.dec < 32 || r.dec === 127 ? '' : r.char)}
                  style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace', cursor: 'copy' }}
                >
                  {r.char}
                </td>
                <td
                  onClick={() => r.html && void copy(r.html)}
                  style={{ fontFamily: 'ui-monospace, monospace', cursor: 'copy' }}
                >
                  {r.html}
                </td>
                <td>{r.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!shown.length ? <p className="tool-live-panel-note">No matching character.</p> : null}
    </div>
  );
}
